import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { AppHeader, type Breadcrumb } from "@/components/AppHeader";
import { AccountMenu } from "@/components/AccountMenu";
import { useNotification } from "@/providers/NotificationProvider";
import {
  useUsageSummary,
  useCreatePortalSession,
} from "@/api/hooks";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";
import { notifications, ui } from "@/strings";
import { UsageLimitBanner } from "@/components/UsageLimitBanner";
import { GCReconnectBanner, GCWriteScopesBanner, useGCGlobalAutoSync } from "@/features/google-classroom";

// Breadcrumb context for child pages to set their breadcrumbs
interface BreadcrumbContextValue {
  breadcrumbs: Breadcrumb[];
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function useBreadcrumbContext() {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumbContext must be used within AuthenticatedLayout");
  }
  return context;
}

interface AuthenticatedLayoutProps {
  children?: ReactNode;
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const navigate = useNavigate();
  const usageQuery = useUsageSummary();
  const portalMutation = useCreatePortalSession();
  const { user, logout } = useAuthStore(useShallow((state) => ({
    user: state.user,
    logout: state.logout,
  })));
  const { show } = useNotification();

  // Auto-sync all stale GC classes on app load
  useGCGlobalAutoSync();

  const usage = usageQuery.data;

  const handlePlanMenuAction = useCallback(() => {
    if (!usage) {
      return;
    }
    if (usage.hasActiveSubscription) {
      if (portalMutation.isPending) {
        return;
      }
      portalMutation.mutate(undefined, {
        onSuccess: ({ url }) => {
          if (typeof window !== "undefined") {
            window.open(url, "_blank", "noopener");
          }
        },
        onError: () => {
          show(notifications.billing.portalFailed, "error");
        },
      });
    } else {
      // Navigate to pricing page for plan selection
      void navigate("/pricing");
    }
  }, [usage, portalMutation, show, navigate]);

  const handleSignOut = useCallback(() => {
    void logout().then(() => {
      void navigate("/");
    });
  }, [logout, navigate]);

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs }}>
      <AppHeader
        breadcrumbs={breadcrumbs}
        actions={
          <AccountMenu
            user={{ email: user?.fullName ?? user?.email ?? ui.headers.account }}
            usage={usage ?? null}
            isProcessingPortal={portalMutation.isPending}
            isSuperuser={user?.isSuperuser ?? false}
            isInternal={user?.isInternal ?? false}
            onPlanAction={handlePlanMenuAction}
            onSignOut={handleSignOut}
          />
        }
      />
      <GCReconnectBanner />
      <GCWriteScopesBanner />
      <UsageLimitBanner usage={usage} />
      {children ?? <Outlet />}
    </BreadcrumbContext.Provider>
  );
}
